import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { 
  ListBaseAssetsGQL, 
  ListUsersGQL, 
  ListUsersQueryVariables,
  CreateCalendarEventGQL,
  CreateCalendarEventMutationVariables,
  BulkEditCalendarEventGQL,
  BulkEditCalendarEventMutationVariables,
  SingleEditInput,
  BaseAssetFragment,
  CalendarEvent_AttendeeFragment
} from 'graphql.generated';
import { catchError, concatMap, exhaustMap, map, of, filter, mergeMap, switchMap, take } from 'rxjs';

import { CREW_ROLE_ATTRIBUTE, EventAttendeeRoles, eventTypeInfoMapV2 } from 'src/app/global.constants';
import {  getResourceChanges } from 'src/app/job/job-helpers';
import { JobsV2EventLocation, jobToolFeature, selectJob } from 'src/app/job/job-tool.reducer';
import { PermissionService } from 'src/app/services/permission.service';

import { OverviewTimelineActions } from '../overview-timeline.actions';
import { overviewTimelineFeature } from '../overview-timeline.feature';

import { CreateEventDialogActions } from './timeline-create-event.actions';
import { formatEventLocation } from './timeline-create-event.reducer';

export const loadEvent = createEffect((
	actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(OverviewTimelineActions.editEventMenuItemSelected),
    concatLatestFrom(() => [
      store.select(selectJob),
      store.select(overviewTimelineFeature.selectTimelineActionsEventId),
    ]),
    map(([ _action, job, eventId ]) => job.events.find((e) => e.id === eventId)),
    map((event) => CreateEventDialogActions.eventLoaded({ event })),
  );
}, { functional: true, dispatch: true });

export const searchAssets = createEffect((
	actions$ = inject(Actions),
	listAssetsGQL = inject(ListBaseAssetsGQL),
	permissionHandler = inject(PermissionService),
) => {
	return actions$.pipe(
		ofType(CreateEventDialogActions.assetAutocompleteSearchFired),
		// Pair the query with the first permission emission; concatLatestFrom
		// drops the action when watchPermission hasn't emitted yet, which would
		// leave autocomplete empty until the user types again.
		concatMap((action) => permissionHandler.watchPermission('assets.view').pipe(
			take(1),
			map((hasAssetsView) => [ action, hasAssetsView ] as const),
		)),
		filter(([, hasAssetsView]) => hasAssetsView),
		// `switchMap`, not `exhaustMap`: a newer query arriving while a fetch is
		// active must supersede it, or suggestions lag the input one keystroke
		// behind until the next trigger.
		switchMap(([{ query }]) => listAssetsGQL.fetch({
      filter: { search: query },
      limit: 5,
    }).pipe(
			map((res) => {
        if (res.loading) {
          return CreateEventDialogActions.searchAssetsLoading();
        }
        return CreateEventDialogActions.searchAssetsSuccess({
          assets: res.data?.assets?.assets || [],
        });
      }),
			catchError((error) => of(CreateEventDialogActions.searchAssetsError({
				error
			}))),
		)),
	)
}, { functional:true, dispatch: true });

export const searchAttendees = createEffect(
  (
    actions$ = inject(Actions),
    listUsersGQL = inject(ListUsersGQL),
  ) => {
    return actions$.pipe(
      ofType(CreateEventDialogActions.attendeesAutocompleteSearchFired),
      exhaustMap(({ query }) => {

        const variables: ListUsersQueryVariables = {
          filter: {
            search: query,
            roleHasAttribute: CREW_ROLE_ATTRIBUTE,
          },
          limit: 5,
        };

        return listUsersGQL.fetch(variables).pipe(
          map((res) => res.data.usersv2.users),
          map((users) => CreateEventDialogActions.searchAttendeesSuccess({ users })),
          catchError((error) => of(CreateEventDialogActions.searchAttendeesError({ error }))),
        );
      }),
    );
  },
  { functional: true, dispatch: true },
);

export const createEvent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  createCalendarEventGQL = inject(CreateCalendarEventGQL),
) => {
  return actions$.pipe(
    ofType(CreateEventDialogActions.mutateEventButtonClicked),
    exhaustMap((action) => of(action).pipe(
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectCreateEventDialog),
        store.select(selectJob),
      ]),
      filter(([_action, createEventDialog, _job]) => !createEventDialog.selectedEventId),
      map(([ _action, createEventDialog, job ]): {
        vars: CreateCalendarEventMutationVariables;
      } => {

        const {
          eventType,
          title,
          assets,
          attendees,
          locations,
        } = createEventDialog;

        const orderedLocations = calculateLocationOrder(locations);

        return {
          vars: { calendarEvents: [
            {
              type: eventType,
              title,
              status: 'required',
              assetIds: assets.map((a) => a.id),
              attendees: attendees.map((a) => ({
                userId: a.user.id,
                role: a.role,
              })),
              jobId: job.id,
              sequentialOrder: job.events.length + 1,
              locations: orderedLocations.map((location) => ({
                estimatedTimeAtLocation: location.estimatedTimeAtLocation,
                ignoreInDuration: location.ignoreInDuration,
                locationId: location.locationId,
                order: location.order,
                type: location.type,
              })),
              colour: eventTypeInfoMapV2[eventType]?.backgroundColor,
              textColour: eventTypeInfoMapV2[eventType]?.textColor,
              revenueGenerating: eventTypeInfoMapV2[eventType]?.revenueGenerating,
              shortTitle: title,
            },
          ] },
        };
      }),
      mergeMap(({ vars }) => createCalendarEventGQL.mutate(vars).pipe(
        map((result) => {
          return CreateEventDialogActions.createEventSuccess({
            createdEvent: result.data.createCalendarEvent.events[0],
            jobId: vars.calendarEvents[0].jobId,
          });
        }),
      )),
      catchError((error) => of(CreateEventDialogActions.createEventError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });

export const updateEvent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  bulkEditCalendarEventGQL = inject(BulkEditCalendarEventGQL),
) => {
  return actions$.pipe(
    ofType(CreateEventDialogActions.mutateEventButtonClicked),
    exhaustMap((action) => of(action).pipe(
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectCreateEventDialog),
        store.select(selectJob),
      ]),
      filter(([_action, createEventDialog, _job]) => Boolean(createEventDialog.selectedEventId)),
      map(([ _action, createEventDialog, job ]): {
        vars: BulkEditCalendarEventMutationVariables;
        jobId: string;
      } => {

        const selectedEvent = job.events.find(e => e.id === createEventDialog.selectedEventId);

        const {
          eventType,
          title,
          status,
          assets,
          attendees,
          locations,
        } = createEventDialog;

        const { added: addAssets, removed: removeAssets } = getResourceChanges({
          original: selectedEvent.assets, modified: assets, keyExtractor: (item: BaseAssetFragment) => item.id
        });

        const eventAttendees = selectedEvent.attendees
          .filter(({ role }) => role !== EventAttendeeRoles.author);

        const { added: attendeesToAdd, removed: attendeesToRemove } = getResourceChanges({
          original: eventAttendees, modified: attendees,
          keyExtractor: (item: CalendarEvent_AttendeeFragment) => item.user.id,
        });

        const orderedLocations = calculateLocationOrder(locations);

        const { added: addLocations, removed: removeLocations, edited: editLocations } = getResourceChanges<JobsV2EventLocation>({
          original: (selectedEvent.locations || []).map(formatEventLocation),
          modified: orderedLocations,
          keyExtractor: (item) => item.id,
          compareFn: (original, modified) => original.order === modified.order &&
            original.estimatedTimeAtLocation === modified.estimatedTimeAtLocation,
          detectEdited: true
        });

        const singleEdit: SingleEditInput = {
          id: createEventDialog.selectedEventId,
          edit: {
            type: eventType,
            title,
            status,
            setAttendees: {
              addAttendees: attendeesToAdd.map((u) => ({ role: u.role, userId: u.user.id })),
              removeAttendees: attendeesToRemove.map((u) => ({ role: u.role, userId: u.user.id })),
            },
            setAssets: {
              addAssets: addAssets.map((a) => a.id),
              removeAssets: removeAssets.map((a) => a.id),
            },
            setLocations: {
              addLocations: addLocations.map((l) => ({
                estimatedTimeAtLocation: l.estimatedTimeAtLocation,
                locationId: l.locationId,
                order: l.order,
                type: l.type,
              })),
              editLocations: editLocations.map((el) => ({
                estimatedTimeAtLocation: el.estimatedTimeAtLocation,
                id: el.id,
                order: el.order,
                type: el.type,
              })),
              removeLocations: removeLocations.map((rl) => rl.id),
            },
          },
        };

        return {
          vars: {
            edits: [singleEdit],
          },
          jobId: job.id,
        };
      }),
      mergeMap(({ vars, jobId }) => bulkEditCalendarEventGQL.mutate(vars).pipe(
        map((result) => CreateEventDialogActions.updateEventSuccess({
          updatedEvent: result.data.bulkEditCalendarEvent.events[0],
          jobId,
        })),
      )),
      catchError((error) => of(CreateEventDialogActions.updateEventError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });

function calculateLocationOrder(locations: JobsV2EventLocation[]) {
  return locations.map((location, index) => ({ ...location, order: index }));
}
